import { Auth0Client, type Auth0ClientOptions } from '@auth0/auth0-spa-js';
import {
  DataClientError,
  isDataClientError,
  type StaffSignedIn,
  type StaffSignInStart,
  type StaffSite,
} from '@kk/domain';

import type { StaffMe } from '@/types/wire';

import { parseStaffSession, STAFF_SESSION_KEYS, staffSessionIsLive, type StaffSession } from './staffSession';
import { acceptInviteWith, staffMeFor, type StaffAuthClient, type StaffAuthDeps } from './StaffAuthClient';

export interface EmailStaffAuthDeps extends StaffAuthDeps {
  /** Which site this is: the admin always asks Google for a company account, and its sign-ins are audited. */
  site: StaffSite;
  /** Auth0's Google connection, reached straight from our own page. Absent: no Google (a company address cannot sign in). */
  google?: { domain: string; clientId: string; audience: string };
  /** Overridable so the tests do not need a browser or a network. */
  createClient?: (opts: Auth0ClientOptions) => Auth0Client;
  /** The browser's own id for the per-device limit and the session binding; random once, then kept. */
  newDeviceId?: () => string;
}

/** What our own sign-in gave back, kept whole: the portal's session is derived from it by `/v1/org/me`. */
interface StoredIdentity extends StaffSignedIn {
  v: 1;
}

/** Refresh a minute before the access token runs out, so no request goes out with one about to lapse. */
const SKEW_MS = 60_000;

/**
 * **OUR OWN SIGN-IN, EMAIL FIRST** (Evan, 2026-09-26; board O00, O00c, O00d). No Auth0 page, name or email on screen:
 *
 * - `startSignIn(email)`: the API answers `google` for a company address — the screen then calls `beginGoogle`, which
 *   goes straight to Google through Auth0's Google connection — and `code` for every other address, which has been
 *   mailed a six-digit code (O00d).
 * - `verifyCode` / the Google return: the API hands back OUR session — a one-hour access token and a refresh token
 *   that works on this computer for 30 days. `getSession` refreshes it quietly; a session that has ended is signed
 *   out, once.
 * - The identity is kept apart from the staff session: somebody signed in who is not staff YET (a first-time
 *   director holding an invite) still has a token to accept it with.
 *
 * A session from before this (Auth0's own, "signed in the old way") is still honoured until it expires, which is why
 * the confirm-email screen stays.
 */
export class EmailStaffAuthClient implements StaffAuthClient {
  readonly kind = 'email' as const;
  private readonly listeners = new Set<(session: StaffSession | null) => void>();
  private readonly auth0: Auth0Client | null;
  private announcedExpiry: string | null = null;
  /** Captured at construction, before any effect can move the address bar (see Auth0StaffAuthClient). */
  private readonly landedAt: string = window.location.href;
  readonly landedFromCallback: boolean = (() => {
    const params = new URL(this.landedAt).searchParams;
    return params.has('state') && (params.has('code') || params.has('error'));
  })();
  private redirect: Promise<StaffSession | null> | null = null;
  /** One refresh at a time: every request that finds the token stale waits on the same one. */
  private refreshing: Promise<StoredIdentity | null> | null = null;

  constructor(private readonly deps: EmailStaffAuthDeps) {
    const make = deps.createClient ?? ((o: Auth0ClientOptions) => new Auth0Client(o));
    this.auth0 = deps.google
      ? make({
          domain: deps.google.domain,
          clientId: deps.google.clientId,
          authorizationParams: { audience: deps.google.audience, redirect_uri: window.location.origin },
          cacheLocation: 'memory',
        })
      : null;
  }

  get site(): StaffSite {
    return this.deps.site;
  }

  /** Google is offered only where it is configured. */
  get googleAvailable(): boolean {
    return this.auth0 !== null;
  }

  // ---- storage ---------------------------------------------------------------------------------------------------

  private emit(session: StaffSession | null): void {
    for (const l of this.listeners) l(session);
  }

  private async deviceId(): Promise<string> {
    const kept = await this.deps.storage.get(STAFF_SESSION_KEYS.deviceId);
    if (kept && /^[A-Za-z0-9_-]{1,64}$/.test(kept)) return kept;
    const made = (this.deps.newDeviceId ?? (() => crypto.randomUUID()))();
    await this.deps.storage.set(STAFF_SESSION_KEYS.deviceId, made);
    return made;
  }

  private async readIdentity(): Promise<StoredIdentity | null> {
    const raw = await this.deps.storage.get(STAFF_SESSION_KEYS.identity);
    if (!raw) return null;
    try {
      const i = JSON.parse(raw) as Partial<StoredIdentity>;
      return i.v === 1 && typeof i.accessToken === 'string' && typeof i.refreshToken === 'string'
        ? (i as StoredIdentity)
        : null;
    } catch {
      return null;
    }
  }

  private async keepIdentity(signedIn: StaffSignedIn): Promise<StoredIdentity> {
    const identity: StoredIdentity = { v: 1, ...signedIn };
    await this.deps.storage.set(STAFF_SESSION_KEYS.identity, JSON.stringify(identity));
    await this.deps.storage.set(STAFF_SESSION_KEYS.lastEmail, signedIn.email);
    return identity;
  }

  private async readStored(): Promise<StaffSession | null> {
    return parseStaffSession(await this.deps.storage.get(STAFF_SESSION_KEYS.session));
  }

  private async clear(): Promise<void> {
    await this.deps.storage.delete(STAFF_SESSION_KEYS.identity);
    await this.deps.storage.delete(STAFF_SESSION_KEYS.session);
  }

  /** Announce a session that has ended, once, so the shell reacts exactly one time. */
  private async announceEnd(key: string): Promise<void> {
    if (this.announcedExpiry === key) return;
    this.announcedExpiry = key;
    await this.clear();
    this.emit(null);
  }

  private sessionOf(identity: StoredIdentity, me: StaffMe): StaffSession {
    return {
      scope: 'staff',
      accessToken: identity.accessToken,
      expiresAt: identity.accessExpiresAt,
      user: me.user,
      memberships: me.memberships,
      ...(me.superAdmin ? { superAdmin: true } : {}),
      ...(me.contentRole ? { contentRole: me.contentRole } : {}),
      ...(me.contentInvite ? { contentInvite: me.contentInvite } : {}),
      ...(me.contentRequest ? { contentRequest: me.contentRequest } : {}),
    };
  }

  /** The identity with an access token good for at least another minute, refreshed if it is not; null once ended. */
  private async liveIdentity(): Promise<StoredIdentity | null> {
    const identity = await this.readIdentity();
    if (!identity) return null;
    if (Date.parse(identity.accessExpiresAt) - SKEW_MS > this.deps.clock.now().getTime()) return identity;
    return (this.refreshing ??= this.refresh(identity).finally(() => {
      this.refreshing = null;
    }));
  }

  private async refresh(identity: StoredIdentity): Promise<StoredIdentity | null> {
    try {
      const renewed = await this.deps.http.request<StaffSignedIn>('POST', '/v1/auth/staff/refresh', {
        auth: false,
        body: { refreshToken: identity.refreshToken, deviceId: await this.deviceId() },
      });
      return await this.keepIdentity(renewed);
    } catch (e) {
      // 401: the session ended (30 days, signed out elsewhere, another computer) — signed out, not a failure
      if (isDataClientError(e) && e.code === 'UNAUTHORIZED') {
        await this.announceEnd(identity.refreshToken);
        return null;
      }
      throw e;
    }
  }

  /** From our identity to the portal's staff session, stored and announced; refuses somebody who is staff of nothing. */
  private async admit(identity: StoredIdentity): Promise<StaffSession> {
    const session = this.sessionOf(identity, await staffMeFor(this.deps.http, identity.accessToken));
    await this.deps.storage.set(STAFF_SESSION_KEYS.session, JSON.stringify(session));
    this.emit(session);
    return session;
  }

  // ---- StaffAuthClient -------------------------------------------------------------------------------------------

  async getSession(): Promise<StaffSession | null> {
    const identity = await this.liveIdentity();
    const stored = await this.readStored();
    if (!identity) {
      // signed in the old way (Auth0's own session): honoured until it runs out, and never refreshed
      if (stored && staffSessionIsLive(stored, this.deps.clock.now())) return stored;
      if (stored) await this.announceEnd(stored.expiresAt);
      return null;
    }
    if (stored && stored.accessToken === identity.accessToken) return stored;
    // a new access token: the same person, re-read so a role or membership that changed shows at once
    return this.admit(identity);
  }

  async startSignIn(email: string): Promise<StaffSignInStart> {
    return this.deps.http.request<StaffSignInStart>('POST', '/v1/auth/staff/start', {
      auth: false,
      body: { email: email.trim(), site: this.deps.site, deviceId: await this.deviceId() },
    });
  }

  async verifyCode(email: string, code: string): Promise<StaffSession> {
    const signedIn = await this.deps.http.request<StaffSignedIn>('POST', '/v1/auth/staff/code', {
      auth: false,
      body: { email: email.trim(), code, site: this.deps.site, deviceId: await this.deviceId() },
    });
    const identity = await this.keepIdentity(signedIn);
    try {
      return await this.admit(identity);
    } catch (e) {
      // the code was RIGHT and the person is staff of nothing: a different answer from a wrong code, so it says so
      if (isDataClientError(e) && e.code === 'INVALID_CREDENTIALS')
        throw new DataClientError('INVALID_CREDENTIALS', 'signed in, but not staff', { reason: 'notStaff' });
      throw e;
    }
  }

  /**
   * Straight to Google through Auth0's Google connection — no Auth0 page. `companyOnly` when the email-first step
   * chose Google for a company address (always on the admin); it rides across the redirect in storage. Leaves the page.
   */
  async beginGoogle(opts: { email?: string; companyOnly: boolean }): Promise<void> {
    if (!this.auth0) throw new DataClientError('NOT_IMPLEMENTED', 'Google sign-in is not configured');
    await this.deps.storage.set(
      STAFF_SESSION_KEYS.googleIntent,
      JSON.stringify({ companyOnly: opts.companyOnly || this.deps.site === 'admin' }),
    );
    await this.auth0.loginWithRedirect({
      authorizationParams: {
        connection: 'google-oauth2',
        prompt: 'select_account',
        ...(opts.email ? { login_hint: opts.email.trim() } : {}),
      },
    });
  }

  completeRedirect(): Promise<StaffSession | null> {
    return (this.redirect ??= this.exchange());
  }

  private async exchange(): Promise<StaffSession | null> {
    if (!this.landedFromCallback || !this.auth0) return null;
    try {
      await this.auth0.handleRedirectCallback(this.landedAt);
      const google = await this.auth0.getTokenSilently();
      const intent = await this.deps.storage.get(STAFF_SESSION_KEYS.googleIntent);
      await this.deps.storage.delete(STAFF_SESSION_KEYS.googleIntent);
      const companyOnly = this.deps.site === 'admin' || (intent ? JSON.parse(intent).companyOnly === true : false);
      const signedIn = await this.deps.http.request<StaffSignedIn>('POST', '/v1/auth/staff/google', {
        token: google,
        body: { site: this.deps.site, deviceId: await this.deviceId(), companyOnly },
      });
      return await this.admit(await this.keepIdentity(signedIn));
    } finally {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  /** A first-time director holds an identity and no membership: the invite is accepted with the identity's token. */
  async acceptInvite(inviteToken: string): Promise<StaffSession | null> {
    const identity = await this.liveIdentity();
    if (!identity) return null;
    const me = await acceptInviteWith(this.deps.http, identity.accessToken, inviteToken);
    const session = this.sessionOf(identity, me);
    await this.deps.storage.set(STAFF_SESSION_KEYS.session, JSON.stringify(session));
    this.emit(session);
    return session;
  }

  async currentEmail(): Promise<string | null> {
    return (await this.readIdentity())?.email ?? (await this.readStored())?.user.email ?? null;
  }

  async lastEmail(): Promise<string | null> {
    return this.deps.storage.get(STAFF_SESSION_KEYS.lastEmail);
  }

  /** Ends OUR session at the API (its access token stops at once) and forgets it here; the last email stays. */
  async signOut(): Promise<void> {
    const identity = await this.readIdentity();
    const current = await this.readStored();
    const email = identity?.email ?? current?.user.email;
    if (email) await this.deps.storage.set(STAFF_SESSION_KEYS.lastEmail, email);
    await this.clear();
    this.emit(null);
    if (identity)
      await this.deps.http
        .request('POST', '/v1/auth/staff/sign-out', { auth: false, body: { refreshToken: identity.refreshToken } })
        .catch((e: unknown) => console.warn('[kk portal] sign-out did not reach the API:', e));
  }

  onChange(listener: (session: StaffSession | null) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}
